/**
 * E.164 validation for SMS recipient numbers (ENG-2302).
 *
 * AWS SNS reads a number without a leading "+" as if it already carried a
 * country code: a bare US `2254659357` was dialled as +225 (Côte d'Ivoire),
 * failed every time and was still billed. So every field that collects an SMS
 * recipient normalizes through here before save:
 *
 * - `+` followed by 8–15 digits (first digit 1–9) is accepted as-is.
 * - A bare 10-digit NANP number (or 11 digits with a leading 1), with any
 *   spaces/dashes/dots/parens, becomes +1XXXXXXXXXX (US/Canada default).
 * - Any other bare number is rejected. A foreign number is never guessed;
 *   at most we suggest the "+" form for the operator to confirm.
 * - Emails and free text are rejected.
 */

export type PhoneResult =
  | { ok: true; e164: string; display: string }
  | { ok: false; reason: string };

export const COUNTRY_CODE_HINT = "Add the country code, e.g. +44 20 7946 0958";
export const EMAIL_REASON =
  "That's an email address; enter a phone number like +1 225 465 9357";
export const FREE_TEXT_REASON =
  "That isn't a phone number; enter one like +1 225 465 9357";

// Formatting characters people type inside phone numbers.
const FORMATTING_CHARS = /[\s\-.()]/g;

/** "+12254659357" → "+1 225 465 9357"; other countries stay "+<digits>". */
export const formatE164ForDisplay = (e164: string): string => {
  const nanp = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (nanp) return `+1 ${nanp[1]} ${nanp[2]} ${nanp[3]}`;
  return e164;
};

const ok = (e164: string): PhoneResult => ({
  ok: true,
  e164,
  display: formatE164ForDisplay(e164),
});

const fail = (reason: string): PhoneResult => ({ ok: false, reason });

// NANP area codes start 2–9; a 10-digit string starting 0/1 is not US/Canada.
const isNanpNational = (digits: string) => /^[2-9]\d{9}$/.test(digits);

// Digits that could already be a full international number (E.164 shape).
const looksInternational = (digits: string) => /^[1-9]\d{7,14}$/.test(digits);

const missingPlusHint = (digits: string): string =>
  looksInternational(digits)
    ? `${COUNTRY_CODE_HINT}. If ${digits} already includes it, did you mean +${digits}?`
    : COUNTRY_CODE_HINT;

export const normalizePhone = (raw: string): PhoneResult => {
  const value = (raw ?? "").trim();
  if (!value) return fail("Enter a phone number");

  // Emails can contain "+" (product+tag@example.com), so check them first.
  if (value.includes("@")) return fail(EMAIL_REASON);

  const compact = value.replace(FORMATTING_CHARS, "");
  if (!/^\+?\d+$/.test(compact)) return fail(FREE_TEXT_REASON);

  if (compact.startsWith("+")) {
    const digits = compact.slice(1);
    if (digits.startsWith("0")) {
      return fail("Country codes don't start with 0, e.g. +44 20 7946 0958");
    }
    if (digits.length < 8) return fail("Too short: + and 8–15 digits");
    if (digits.length > 15) return fail("Too long: + and 8–15 digits");
    return ok(`+${digits}`);
  }

  const digits = compact;
  if (digits.startsWith("00")) {
    const rest = digits.slice(2);
    return fail(
      looksInternational(rest)
        ? `Use + instead of 00: did you mean +${rest}?`
        : COUNTRY_CODE_HINT
    );
  }
  if (digits.length === 10 && isNanpNational(digits)) return ok(`+1${digits}`);
  if (digits.length === 11 && digits.startsWith("1")) {
    const national = digits.slice(1);
    if (isNanpNational(national)) return ok(`+1${national}`);
  }
  if (digits.length < 10) {
    return fail(
      "Too short: enter 10 digits for US/Canada, or + and the country code"
    );
  }
  return fail(missingPlusHint(digits));
};

export interface PhoneListEntry {
  raw: string;
  result: PhoneResult;
}

export interface PhoneList {
  entries: PhoneListEntry[];
  /** Normalized, de-duplicated E.164 numbers in entry order. */
  valid: string[];
  invalid: PhoneListEntry[];
  hasInvalid: boolean;
}

/** Split on commas, semicolons and newlines; blank entries are ignored. */
export const splitPhoneList = (raw: string | string[] | null | undefined) => {
  const parts = Array.isArray(raw) ? raw : String(raw ?? "").split(/[,;\n]/);
  return parts.map((part) => String(part ?? "").trim()).filter(Boolean);
};

export const parsePhoneList = (
  raw: string | string[] | null | undefined
): PhoneList => {
  const entries = splitPhoneList(raw).map((part) => ({
    raw: part,
    result: normalizePhone(part),
  }));
  const valid: string[] = [];
  entries.forEach(({ result }) => {
    if (result.ok && !valid.includes(result.e164)) valid.push(result.e164);
  });
  const invalid = entries.filter(({ result }) => !result.ok);
  return { entries, valid, invalid, hasInvalid: invalid.length > 0 };
};

/**
 * The separator a stored comma-separated value already uses: ", " if any
 * comma is followed by whitespace, "," otherwise (also the default for new
 * values, matching the field's historic "comma separated, no spaces" hint).
 */
export const detectListSeparator = (raw: string | null | undefined) =>
  /,\s/.test(String(raw ?? "")) ? ", " : ",";

/** One-line summary for helper text, e.g. "Will text +1 225 465 9357". */
export const describePhoneList = (list: PhoneList): string =>
  list.valid.length
    ? `Will text ${list.valid.map(formatE164ForDisplay).join(", ")}`
    : "";
